import { computed, ref } from 'vue';

/* ------------------------------------------------------------------------------------------------
 * Onboarding tour state.
 *
 * A small step-machine that drives the coach-mark overlay (see onboarding-tour.vue). Steps target
 * real elements by a `data-tour="…"` attribute. First-run is gated in localStorage — like the
 * What's New popup — but the tour stays relaunchable from the user menu.
 * ---------------------------------------------------------------------------------------------- */

const STORAGE_KEY = 'onboarding-seen-version';
// Bump this to re-show the tour to everyone after a meaningful change.
const ONBOARDING_VERSION = '2';

export interface OnboardingStep {
   /** Stable id */
   id: string;
   /** CSS selector for the element to spotlight (a `[data-tour="…"]` hook) */
   target: string;
   title: string;
   body: string;
   /** floating-ui placement for the tooltip relative to the target */
   placement?: string;
}

const steps = ref<OnboardingStep[]>([
   {
      id: 'search',
      target: '[data-tour="search"]',
      title: 'Search across everything',
      body: 'Start here — full-text search over every indexed document. Type a question or keyword, or pick one of the example searches.',
      placement: 'bottom',
   },
   {
      id: 'doclist',
      target: '[data-tour="doclist"]',
      title: 'Switch document lists',
      body: 'Use this to jump between Document Search, the NMED Document Browser, and your Saved Documents.',
      placement: 'bottom-start',
   },
   {
      id: 'nav',
      target: '[data-tour="nav-menu"]',
      title: 'Projects & Facilities',
      body: 'Jump to your Projects and browse Facilities right here in the menu.',
      placement: 'bottom',
   },
   {
      id: 'user',
      target: '[data-tour="user"]',
      title: 'Account & settings',
      body: 'Your profile lives here — open it for Settings, Admin tools, and sign-out.',
      placement: 'right-end',
   },
   {
      id: 'luma',
      target: '[data-tour="luma"]',
      title: 'Meet Luma',
      body: 'Luma, your AI assistant, is one tap away on any page. Ask it to summarize, compare, or find documents for you.',
      placement: 'left-end',
   },
]);

const isActive = ref(false);
const currentIndex = ref(0);

export function useOnboarding() {
   const currentStep = computed(() => steps.value[currentIndex.value] ?? null);
   const stepCount = computed(() => steps.value.length);
   const isFirst = computed(() => currentIndex.value === 0);
   const isLast = computed(() => currentIndex.value === steps.value.length - 1);

   function hasSeen() {
      return localStorage.getItem(STORAGE_KEY) === ONBOARDING_VERSION;
   }

   function markSeen() {
      localStorage.setItem(STORAGE_KEY, ONBOARDING_VERSION);
   }

   /** Start (or restart) the tour from a given step. */
   function start(index = 0) {
      currentIndex.value = index;
      isActive.value = true;
   }

   function goTo(index: number) {
      currentIndex.value = Math.max(0, Math.min(index, steps.value.length - 1));
   }

   function next() {
      if (isLast.value)
         finish();
      else
         currentIndex.value += 1;
   }

   function prev() {
      if (!isFirst.value)
         currentIndex.value -= 1;
   }

   /** Dismiss without finishing — still marks seen so it doesn't nag. */
   function skip() {
      isActive.value = false;
      markSeen();
   }

   function finish() {
      isActive.value = false;
      markSeen();
   }

   /** Auto-run once for users who haven't seen this version yet. */
   function maybeAutoStart() {
      if (!hasSeen())
         start(0);
   }

   return {
      steps,
      isActive,
      currentIndex,
      currentStep,
      stepCount,
      isFirst,
      isLast,
      hasSeen,
      start,
      goTo,
      next,
      prev,
      skip,
      finish,
      maybeAutoStart,
   };
}
